// addNamedDefault — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'add-named-default', namedDefault })` op (DSE2 §3.B5).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B5.
//
// mssql-only. Two synthesizer shapes:
//   - inline (modern, recommended):
//       `ALTER TABLE … ADD CONSTRAINT df_name DEFAULT (expr) FOR col`.
//     Requires `column` plus a CREATE/ALTER TABLE source statement.
//   - standalone (deprecated since SQL Server 2008):
//       `CREATE DEFAULT df_name AS expr`. Free-floating object — must be
//     bound to a column via `sp_bindefault` separately. Opt in by setting
//     `standalone: true` on the def. The standalone form does not require
//     a CREATE/ALTER TABLE source.
//
// pgsql / mysql / oracle / sqlite reject this op — defaults on those
// engines are column attributes (Slice A1's `alter-column` op with
// `change.default`), not first-class named objects. The form is therefore
// gated to mssql; non-mssql engines surface a tooltip pointing at the
// column form.

import type {
  DdlEdit,
  NamedDefaultDef,
} from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type AddNamedDefaultArgs = {
  readonly namedDefault: NamedDefaultDef;
};

export function buildAddNamedDefaultEdit(args: AddNamedDefaultArgs): DdlEdit {
  return { op: 'add-named-default', namedDefault: args.namedDefault };
}

export async function commitAddNamedDefault(
  ctx: VqeDdlEditContext,
  args: AddNamedDefaultArgs,
): Promise<void> {
  const edit = buildAddNamedDefaultEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

/** mssql is the only engine with first-class named DEFAULT objects. Other
 *  engines surface a documented tooltip pointing at the column form. */
export function isAddNamedDefaultSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function getAddNamedDefaultUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') return '';
  const label = engineLabel(engine);
  return (
    `Named DEFAULT objects are a SQL Server-specific concept. On ${label}, set `
    + `defaults via the column form (Default expression field).`
  );
}

function engineLabel(engine: SqlEngine): string {
  switch (engine) {
    case 'postgres': return 'PostgreSQL';
    case 'mysql': return 'MySQL';
    case 'oracle': return 'Oracle';
    case 'sqlite': return 'SQLite';
    case 'mssql': return 'SQL Server';
  }
}

/** Pre-flight validator mirroring DSE2's `validateDef` rules so the form can
 *  surface engine errors before the synthesizer would throw. Returns null
 *  when the spec is acceptable. */
export function validateNamedDefaultForEngine(
  engine: SqlEngine,
  def: NamedDefaultDef,
): string | null {
  if (engine !== 'mssql') return getAddNamedDefaultUnsupportedTooltip(engine);
  if (!def.name || def.name.trim().length === 0) {
    return 'Named DEFAULT requires a non-empty name.';
  }
  if (!def.expression || def.expression.trim().length === 0) {
    return 'Named DEFAULT requires a non-empty expression.';
  }
  if (!def.standalone) {
    if (!def.column || def.column.trim().length === 0) {
      return (
        'Inline form requires a column to bind the default to. '
        + 'Use the standalone form for a free-floating CREATE DEFAULT object.'
      );
    }
  }
  return null;
}
